import React from 'react';

import SeminarLandingPage, { SeminarLandingPageConfig } from '@/src/components/feature/SeminarLandingPage';
import {
  getSeminarLandingPageStaticProps,
  SeminarLandingPageStaticProps,
} from '@/src/components/feature/SeminarLandingPage/getStaticProps';

import HeroImage from '@/public/newyork-seminar/hero.webp';
import HeroMobileImage from '@/public/newyork-seminar/hero_mobile.webp';
import StrategyClassroomImage from '@/public/newyork-seminar/strategy_classroom.webp';
import StrategyNyuStreetImage from '@/public/newyork-seminar/strategy_nyu_street.webp';
import PersonaRelocatingFamilyImage from '@/public/newyork-seminar/persona_relocating_family.webp';
import PersonaStudentImage from '@/public/newyork-seminar/persona_student.webp';
import PersonaInvestorImage from '@/public/newyork-seminar/persona_investor.webp';
import FaqKeysImage from '@/public/newyork-seminar/faq_keys.webp';

// Full city names, not the short location keys — listingApi.search() expands each name to its
// keywords (name + key) and scopes the match to the city's own state (see funwooApis
// src/resource/listing/city-filter.ts). The key 'BRO' is shared by Brooklyn (NY) and Brookline
// (MA), so passing keys would let Brookline listings leak onto this page; 'Brooklyn' is unambiguous.
// Unlike Phoenix, New York has no single city that covers the whole metro area.
const NEWYORK_CITIES = ['Manhattan', 'New York', 'Brooklyn', 'Queens'];

// State key as returned by optionApi.getOptionsForBuy()'s usCitiesData — used to fetch the /buy
// link's city list fresh on every build instead of hardcoding it (see getStaticProps below).
const NEWYORK_STATE = 'NY';

const config: SeminarLandingPageConfig = {
  slug: 'newyork-seminar',

  heroAlt: '紐約',
  heroImage: HeroImage,
  heroImageMobile: HeroMobileImage,
  heroTitleDesktop: '《美國置產說明會》留學家庭 · 以房養學',
  heroTitleMobileLines: ['美國置產說明會', '留學家庭 · 以房養學'],
  heroSubhead:
    '美國匯聚全球頂尖教育資源，紐約與波士頓更是許多留學家庭長期關注的城市。從 Columbia、NYU 到 Harvard、MIT，不僅擁有世界級學府，也形成穩定的人口、租賃與住宅需求。對於計畫送孩子赴美留學的家庭而言，房子不只是孩子求學期間的居所，更可以成為家庭資產配置的一部分。透過提前規劃，在滿足居住需求的同時，也能將原本支付租金的支出，轉化為長期持有的房產資產。',

  whyTabTitle: '為何選擇紐約',
  strategies: [
    {
      image: StrategyClassroomImage,
      imageFirst: true,
      title: '為什麼買比租更聰明',
      body: (
        <>
          <p>
            <strong>一、財務｜以房養學現金流</strong>
            <br />
            房貸月付對照宿舍與校外租屋成本，畢業後出租或出售的退場路徑一起試算。錢流去哪、幾年回本，攤在同一張表上看。
          </p>
          <p>
            <strong>二、安心｜學區與居住安全</strong>
            <br />
            父母最深的焦慮，是孩子住得好不好、安不安全。治安地圖、學區評比、通勤實拍——把「掌控感」交回你手上。
          </p>
          <p>
            <strong>三、專業｜稅務與持有結構</strong>
            <br />
            個人或公司持有？稅務上有哪些區別？未來傳承的方式等等，FUNWOO
            美國專業會計師及律師為你解答。這些是顧問的活，不是仲介帶看能給的。
          </p>
        </>
      ),
    },
    {
      image: StrategyNyuStreetImage,
      imageFirst: false,
      title: '為留學家庭挑的房子',
      body: (
        <>
          <p>
            送孩子出國那天，你會先煩惱住哪裡。宿舍抽不到、校外租屋押金一去不回、房東半年漲一次，租四年下來，一筆足以付頭期款的錢，換來一次次搬家。
          </p>
          <p>
            孩子赴美留學，選擇住宅時考量的不只是距離學校多遠，更包含生活機能、通勤便利性、居住安全、社區環境與未來出租需求。
          </p>
          <p>
            FUNWOO
            從留學生家庭的實際需求出發，依照學校位置、生活圈與家庭預算，協助篩選適合長期持有的住宅。從孩子入住期間的居住品質，到未來出租、轉售或家庭持有的可能性，讓每一間房子的選擇，都不只是為了「住得方便」，也兼顧家庭長期資產規劃，選對房子，讓孩子安心求學，也讓家庭的每一筆投入更有價值。
          </p>
        </>
      ),
    },
  ],

  listingsTabTitle: '紐約精選物件',
  listingsHeadline: '留學生家長首選建案',
  listingsDescription: (
    <>
      以下部分房源供參考，FUNWOO 將依照客戶購屋需求，媒合市場上最佳建案。
      <br />
      <a href={'#contact-us'} className={'underline'}>
        點選此填寫美加置產購屋需求
      </a>
    </>
  ),
  listingCities: NEWYORK_CITIES,
  // Figma's CTA button here still reads "查看更多鳳凰城房源" verbatim (copy-pasted from the Phoenix
  // page and never localized) — corrected to the New York equivalent.
  listingsButtonLabel: '查看更多紐約房源',

  // Figma's tab bar instance is likewise still labelled for Phoenix ("鳳凰城買家" etc.) — corrected
  // to follow the same "{city}買家" naming used on the Phoenix page.
  buyersTabTitle: '紐約買家',
  buyersHeadline: '從諮詢到在美國有了家',
  personas: [
    {
      image: PersonaRelocatingFamilyImage,
      title: '移居海外家庭',
      content:
        '移居海外，重新規劃一家人的生活方式與未來。從居住環境、交通機能、學區選擇，到社區品質與房產條件，每項都影響家庭長期在海外的生活。',
    },
    {
      image: PersonaStudentImage,
      title: '有教育需求者',
      content: '以國際學校家長為最大宗，因規劃兒女美國留學，長期關注當地學區房或近知名大學附近套房。',
    },
    {
      image: PersonaInvestorImage,
      title: '美國學區房投資人',
      // Figma's source string reads "世界個挼專業人才", which isn't valid Chinese — read as a
      // typo/OCR artifact for "世界各地專業人才" (professionals from around the world).
      content: '美國優質學區長期聚集學生家庭與世界各地專業人才，穩定的居住需求與高租金。',
    },
  ],

  faqImage: FaqKeysImage,
  qaData: [
    {
      question: '美國哪些學區區域最值得關注？',
      answer:
        '紐約不僅是全球金融與文化中心，也匯聚多所國際知名大學，對於計畫讓孩子赴美留學的家庭而言，「就學期間自住、畢業後轉為出租」也是常見的置產思維。以紐約市為例，可關注以下知名學校及周邊生活圈：<br />' +
        '• NYU 紐約大學｜曼哈頓 Greenwich Village／Washington Square<br />' +
        '• Columbia University 哥倫比亞大學｜曼哈頓 Morningside Heights<br />' +
        '• Parsons School of Design 帕森設計學院｜曼哈頓 Greenwich Village<br />' +
        'FUNWOO 提供多種鄰近紐約知名學區與大學生活圈的精選物件，協助家長依照孩子的就學地點、通勤便利性與未來出租需求，尋找更符合家庭長期規劃的美國房產。',
    },
    {
      question: '我人在台灣，可以完成購屋流程嗎？',
      answer:
        '購置美國不動產無需親自飛抵美國開戶，可以從海外將房款直接打到買賣合約上指定有合法立案的 Escrow 公司 (履約保證信託專戶），通常 1-5 天內 Escrow 會收到匯款並開立收據給買方。',
    },
    {
      question: '孩子畢業後房子怎麼辦？',
      answer:
        '許多家長購買美國房產時，除滿足孩子留學期間的居住需求，也會提前規劃未來的資產運用。孩子畢業後，房產可轉為出租、出售，或持續作為海外資產配置。<br /><br />' +
        'FUNWOO 提供從購屋、交屋到物業管理與媒合高品質租客與後續管理的一條龍服務。專業顧問具備美國當地房地產經紀人執照及物業管理經驗。',
    },
  ],

  ogTitle: '《美國置產說明會》留學家庭 · 以房養學 - FUNWOO 德載國際不動產',
  ogDescription: '美國紐約置產說明會報名，當地執業房產顧問為您量身打造一條龍服務',
  ogType: 'funwoo.newyork-seminar',
};

const NewYorkSeminar: React.FC<SeminarLandingPageStaticProps> = ({ listings, userStories, buyLinkCities }) => (
  <SeminarLandingPage config={config} listings={listings} userStories={userStories} buyLinkCities={buyLinkCities} />
);

export default NewYorkSeminar;

export const getStaticProps = getSeminarLandingPageStaticProps(NEWYORK_CITIES, NEWYORK_STATE);
